import { useEffect, useRef, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import {
    Box,
    ClickAwayListener,
    Grid,
    List,
    ListItemButton,
    ListItemText,
    Paper,
    Popper,
    Typography,
    useMediaQuery,
    Button
} from '@mui/material';

import Transitions from 'components/Transitions';

import useConfig from 'hooks/useConfig';

const languageValues = [
    { text: 'Español', abr: 'ES', lang: 'es-ES' },
    { text: 'English', abr: 'UK', lang: 'en-GB' },
    { text: 'Français', abr: 'FR', lang: 'fr-FR' },
    { text: 'Português', abr: 'PT', lang: 'pt-PT' },
    { text: 'Italiano', abr: 'IT', lang: 'it' },
    { text: 'Deutsch', abr: 'DE', lang: 'de' },
    { text: 'Português Brasileiro', abr: 'BR', lang: 'pt-BR' }
];

const LocalizationSection = () => {
    const { borderRadius, locale, onChangeLocale } = useConfig();

    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));

    const [open, setOpen] = useState(false);
    const anchorRef = useRef<any>(null);
    const [language, setLanguage] = useState<string>(locale);

    const handleListItemClick = (
        event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLDivElement, MouseEvent> | undefined,
        lng: { text: string; abr: string; lang: string }
    ) => {
        setLanguage(lng.text);
        onChangeLocale(lng.lang);
        setOpen(false);
    };

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    useEffect(() => {
        const langValue = languageValues.find((v) => v.lang.includes(locale));

        setLanguage(langValue?.text || languageValues[0].text);
    }, [locale]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 2,
                    [theme.breakpoints.down('md')]: {
                        ml: 1
                    }
                }}
            >
                <Button
                    size="small"
                    variant="outlined"
                    aria-label={`${language} - cambiar idioma`}
                    aria-expanded={open}
                    sx={{
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                        color: theme.palette.primary.dark,
                        transition: 'all .2s ease-in-out',
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            borderColor: theme.palette.primary.dark,
                            background: theme.palette.primary.dark,
                            color: theme.palette.primary.light
                        }
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="inherit"
                >
                    <Typography variant="h5" color="inherit">
                        {language}
                    </Typography>
                </Button>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom-start' : 'bottom'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                modifiers={[
                    {
                        name: 'offset',
                        options: {
                            offset: [matchesXs ? 0 : 0, 20]
                        }
                    }
                ]}
                sx={{ zIndex: '999' }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top-left' : 'top'} in={open} {...TransitionProps}>
                            <Paper elevation={16}>
                                {open && (
                                    <List
                                        component="nav"
                                        sx={{
                                            width: '100%',
                                            minWidth: 200,
                                            maxWidth: 280,
                                            bgcolor: theme.palette.background.paper,
                                            borderRadius: `${borderRadius}px`,
                                            [theme.breakpoints.down('md')]: {
                                                maxWidth: 250
                                            }
                                        }}
                                    >
                                        {' '}
                                        {languageValues.map((value, index) => (
                                            <ListItemButton
                                                key={index}
                                                selected={language === value.text}
                                                onClick={(event) => handleListItemClick(event, value)}
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Grid container>
                                                            <Typography color="textPrimary">{value.text}</Typography>
                                                            <Typography
                                                                variant="caption"
                                                                color="textSecondary"
                                                                sx={{ ml: '8px', mt: '3px' }}
                                                            >
                                                                ({value.abr})
                                                            </Typography>
                                                        </Grid>
                                                    }
                                                />
                                            </ListItemButton>
                                        ))}
                                    </List>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default LocalizationSection;
